import React, { Component } from 'react';
import { Footer } from '../components/Footer';
import { MailIcon, ExternalLinkIcon, CheckCircle2Icon, CircleDashedIcon, MinusCircleIcon, ClockIcon } from 'lucide-react';
type CertStatus = 'Certified' | 'In Progress' | 'Not Pursued';
const StatusBadge = ({
  status
}: {
  status: CertStatus;
}) => {
  const styles: Record<CertStatus, {
    cls: string;
    Icon: ComponentType<any>;
  }> = {
    Certified: {
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      Icon: CheckCircle2Icon
    },
    'In Progress': {
      cls: 'bg-amber-50 text-amber-700 border-amber-200',
      Icon: CircleDashedIcon
    },
    'Not Pursued': {
      cls: 'bg-gray-100 text-gray-600 border-gray-200',
      Icon: MinusCircleIcon
    }
  };
  const {
    cls,
    Icon
  } = styles[status];
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`} data-id="element-883" data-mp="1dyy0z80dv1ar">
      <Icon className="w-3.5 h-3.5" strokeWidth={2} data-id="element-884" data-mp="18yk3gjwer9qz" />
      {status}
    </span>;
};
const SectionTitle = ({
  title
}: {
  title: string;
}) => <h2 className="text-2xl md:text-3xl font-serif font-normal text-gray-600 mb-6 text-center" data-id="element-885" data-mp="1uiyswydmy9rx">
    {title}
  </h2>;
const Card = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`bg-white/80 backdrop-blur-sm rounded-xl p-8 shadow-sm border border-gray-100 ${className}`} data-id="element-886" data-mp="1litmaqg9nfet">
    {children}
  </div>;
export const Security = () => {
  // Certifications — only include items with explicit status. No SOC 2 / ISO 27001 claims.
  const certifications: {
    name: string;
    status: CertStatus;
    note: string;
  }[] = [{
    name: 'SOC 2 Type II',
    status: 'Not Pursued',
    note: 'Available on request once we begin a formal audit cycle. Contact security@nisa.coach for our current control summary.'
  }, {
    name: 'ISO 27001',
    status: 'Not Pursued',
    note: 'Not currently on our roadmap. Equivalent controls are documented and available for review.'
  }, {
    name: 'GDPR alignment',
    status: 'In Progress',
    note: 'We honor data subject access and deletion requests today; formal documentation is being finalized.'
  }, {
    name: 'FERPA alignment',
    status: 'In Progress',
    note: 'Nisa is designed for use under district FERPA frameworks. We sign customer DPAs as a school official under direct control.'
  }];
  const subProcessors = ['Vercel', 'AWS', 'Neon', 'Anthropic', 'OpenAI', 'Google', 'SendGrid', 'Twilio', 'Inngest', 'Langfuse', 'PostHog'];
  const documents = [{
    title: 'Privacy Policy',
    description: 'How we collect, use, and protect personal information.',
    href: '/privacy',
    external: false
  }, {
    title: 'Terms of Service',
    description: 'The terms that govern use of Nisa.',
    href: '/terms',
    external: false
  }, {
    title: 'Sub-processors list',
    description: 'View the third-party services Nisa uses and the data each handles.',
    href: '/sub-processors',
    external: false
  }];
  return <div className="flex flex-col min-h-screen" data-id="element-887" data-mp="on4qx07i2ze1">
      <main className="py-section-mobile md:py-section-desktop relative flex-grow overflow-hidden" data-id="element-888" data-mp="olab20b35xgt">
        {/* Background pattern */}
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] -z-10" data-id="element-889" data-mp="3t24kop04l23s" />
        {/* XL Cyan gradient blob */}
        <div className="absolute -left-[300px] top-[10%] w-[1400px] h-[1400px] bg-gradient-to-tr from-cyan-500/15 via-primary-300/10 to-cyan-200/15 rounded-[50%_50%_45%_55%] blur-3xl -z-10" data-id="element-890" data-mp="x0o2v24z3o3" />
        {/* Floating gradient blobs */}
        <div className="absolute top-0 right-1/3 w-[900px] h-[900px] bg-gradient-to-br from-cyan-400/10 via-teal-400/10 to-blue-400/10 rounded-[60%_40%_55%_45%] blur-3xl -z-10" data-id="element-891" data-mp="aowowdvlx7w0" />
        <div className="absolute top-[30%] -left-[200px] w-[800px] h-[800px] bg-gradient-to-tr from-purple-400/10 via-violet-400/10 to-fuchsia-400/10 rounded-[40%_60%_45%_55%] blur-3xl -z-10 animate-float" style={{
        animationDelay: '2s'
      }} data-id="element-892" data-mp="3ebdufle6mb2s" />
        <div className="absolute top-[60%] right-0 w-[750px] h-[750px] bg-gradient-to-bl from-amber-400/10 via-orange-400/10 to-red-400/10 rounded-[55%_45%_60%_40%] blur-3xl -z-10 animate-float" style={{
        animationDelay: '1.5s'
      }} data-id="element-893" data-mp="1tnbe8ct991mm" />
        <div className="absolute bottom-0 left-1/4 w-[700px] h-[700px] bg-gradient-to-r from-emerald-400/10 via-green-400/10 to-teal-400/10 rounded-[45%_55%_50%_50%] blur-3xl -z-10 animate-float" style={{
        animationDelay: '3s'
      }} data-id="element-894" data-mp="1ylw8lvltsr35" />
        {/* Content container */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-895" data-mp="i85yo880z8ll">
          {/* Hero */}
          <h1 className="text-4xl md:text-5xl font-serif font-normal text-cyan-900 mb-6 text-center" data-id="element-896" data-mp="e6uisueye1ql">
            Security at Nisa
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto text-center leading-relaxed mb-16" data-id="element-897" data-mp="43yoj15t8t8i">
            Coaches and the teachers they support trust Nisa with sensitive
            classroom work. We treat that data with the same care, and build
            Nisa with practices that make it straightforward for your IT and
            compliance teams to review.
          </p>

          <div className="space-y-12" data-id="element-898" data-mp="3gl3w7pynyxzv">
            {/* Data Handling */}
            <section id="data-handling" data-id="element-899" data-mp="3livqgqms6w78">
              <SectionTitle title="Data handling" data-id="element-900" data-mp="37ib45khnqi8z" />
              <Card data-id="element-901" data-mp="37jmi2d4th6dt">
                <div className="grid md:grid-cols-2 gap-6" data-id="element-902" data-mp="37k5p2ysxiad2">
                  <div data-id="element-903" data-mp="3cgmh443918vf">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-904" data-mp="3chxrimr79evi">
                      What we collect
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-905" data-mp="1aq14afvo6gkw">
                      Coach and administrator account details, coaching notes
                      and artifacts uploaded by coaches, and product usage data
                      that helps us improve the experience.
                    </p>
                  </div>
                  <div data-id="element-906" data-mp="z9wlnwq0bem3">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-907" data-mp="zb7w2fdyjkm6">
                      What we don't collect
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-908" data-mp="1rz3t5l7fvejh">
                      We do not require, request, or store student PII. We ask
                      coaches to keep student identifiers out of uploaded notes,
                      and we provide guidance in-product to support that.
                    </p>
                  </div>
                  <div data-id="element-909" data-mp="1d9xvi1o91wdp">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-910" data-mp="1d8czs8q787e9">
                      Where it's stored
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-911" data-mp="25yv0j33g9woi">
                      Customer data is stored in U.S.-based managed cloud
                      services (AWS and Neon), with logical isolation per
                      organization. Access is restricted to a small set of named
                      engineers.
                    </p>
                  </div>
                  <div data-id="element-912" data-mp="2s7jzi8zr3uro">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-913" data-mp="2s7tq9ysme5yf">
                      Retention
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-914" data-mp="2w8lxynlug2sl">
                      We retain customer data for the life of the account.
                      Customers may request export or deletion at any time; we
                      complete verified deletion requests within 30 days.
                    </p>
                  </div>
                </div>
              </Card>
            </section>

            {/* Encryption */}
            <section id="encryption" data-id="element-915" data-mp="3jypsm5h20s7g">
              <SectionTitle title="Encryption" data-id="element-916" data-mp="3jzrdt2rsav2w" />
              <Card data-id="element-917" data-mp="3jwdb1tf7kjux">
                <div className="grid md:grid-cols-2 gap-6" data-id="element-918" data-mp="940llf86x0qm">
                  <div data-id="element-919" data-mp="96da6b8ge9cn">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1" data-id="element-920" data-mp="52p11o46b5pa">
                      In transit
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-921" data-mp="j1exr2n2z3d4">
                      All connections to Nisa use TLS 1.2 or higher. HTTPS is
                      enforced end-to-end across the application and APIs.
                    </p>
                  </div>
                  <div data-id="element-922" data-mp="3hkdih6ciwsjl">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1" data-id="element-923" data-mp="3hkvdjzng90px">
                      At rest
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-924" data-mp="4wym05wk16u">
                      Data at rest is encrypted with AES-256 using
                      managed-service envelope encryption across our database
                      and object storage.
                    </p>
                  </div>
                </div>
              </Card>
            </section>

            {/* Authentication & Access */}
            <section id="access" data-id="element-925" data-mp="1ea8ppdntvxax">
              <SectionTitle title="Authentication & access" data-id="element-926" data-mp="1j20at8o7tato" />
              <Card data-id="element-927" data-mp="1j3l5cyaj4trj">
                <ul className="space-y-4" data-id="element-928" data-mp="15avheobfxers">
                  <li className="flex gap-3" data-id="element-929" data-mp="15bws2rojnz8z">
                    <CheckCircle2Icon className="w-5 h-5 text-cyan-600 shrink-0 mt-0.5" strokeWidth={1.75} data-id="element-930" data-mp="15927n05j6efk" />
                    <div data-id="element-931" data-mp="1x2ksg1ie90ll">
                      <span className="font-medium text-gray-800" data-id="element-932" data-mp="1x08es0jo5cec">
                        Google OAuth
                      </span>{' '}
                      <span className="text-gray-700" data-id="element-933" data-mp="1o2640ta4v5dv">
                        for individual coach accounts, with no Nisa-managed
                        passwords by default.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-3" data-id="element-934" data-mp="coanph7hsql2">
                    <CheckCircle2Icon className="w-5 h-5 text-cyan-600 shrink-0 mt-0.5" strokeWidth={1.75} data-id="element-935" data-mp="hlkmhngruelk" />
                    <div data-id="element-936" data-mp="3mvq04zakeapg">
                      <span className="font-medium text-gray-800" data-id="element-937" data-mp="3mw7vzutbemwu">
                        SAML SSO
                      </span>{' '}
                      <span className="text-gray-700" data-id="element-938" data-mp="394b4nszh0h1i">
                        available for school and district plans, with
                        IdP-managed provisioning and deprovisioning.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-3" data-id="element-939" data-mp="346jbywr04qw9">
                    <CheckCircle2Icon className="w-5 h-5 text-cyan-600 shrink-0 mt-0.5" strokeWidth={1.75} data-id="element-940" data-mp="3433wvtxbvhuu" />
                    <div data-id="element-941" data-mp="2v5bprd1rfgij">
                      <span className="font-medium text-gray-800" data-id="element-942" data-mp="2v6mtxdz4aqa6">
                        Role-based access
                      </span>{' '}
                      <span className="text-gray-700" data-id="element-943" data-mp="tdou2y6xaxfq">
                        separates coach, school leader, and district admin
                        permissions so each role sees only what it needs.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-3" data-id="element-944" data-mp="1c9fgcjgiw55n">
                    <CheckCircle2Icon className="w-5 h-5 text-cyan-600 shrink-0 mt-0.5" strokeWidth={1.75} data-id="element-945" data-mp="1cb8k9qzr4b9y" />
                    <div data-id="element-946" data-mp="1m6sf0a2kx3x4">
                      <span className="font-medium text-gray-800" data-id="element-947" data-mp="1m6acwwt8jvha">
                        Least-privilege internal access
                      </span>{' '}
                      <span className="text-gray-700" data-id="element-948" data-mp="1v91nyvi78p8e">
                        production access is limited to a small, named
                        engineering group and reviewed on a regular cadence.
                      </span>
                    </div>
                  </li>
                </ul>
              </Card>
            </section>

            {/* Sub-processors */}
            <section id="sub-processors" data-id="element-949" data-mp="1vxorz0k9gq3g">
              <SectionTitle title="Sub-processors" data-id="element-950" data-mp="1vyqbltfcekws" />
              <Card data-id="element-951" data-mp="20wrxsn82f0ch">
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-952" data-mp="20w8f34alc7xe">
                  Nisa relies on a focused set of service providers to operate
                  the product. We maintain a current list of these
                  sub-processors and the data each one handles.
                </p>
                <div className="flex flex-wrap gap-2 mb-6" data-id="element-953" data-mp="2ex2onq9dkexn">
                  {subProcessors.map(s => <span key={s} className="px-3 py-1 rounded-full text-sm bg-gray-50 text-gray-700 border border-gray-200" data-id="element-954" data-mp="2juugyldxm32d">
                      {s}
                    </span>)}
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-4 text-sm text-gray-600" data-id="element-955" data-mp="2yks5x3joqwsm">
                  Full sub-processor list available on request to{' '}
                  <a href="mailto:security@nisa.coach" className="text-cyan-600 hover:text-cyan-700 underline underline-offset-2" data-id="element-956" data-mp="32jr8wiluwjhf">
                    security@nisa.coach
                  </a>
                  .
                </div>
              </Card>
            </section>

            {/* Penetration Testing */}
            <section id="pen-testing" data-id="element-957" data-mp="d0tttn9t4rte">
              <SectionTitle title="Penetration testing" data-id="element-958" data-mp="1amhp24uj143c" />
              <Card data-id="element-959" data-mp="1aly74o4vmfpb">
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-960" data-mp="1aokdikg16p5y">
                  Independent penetration testing is part of our security
                  roadmap. We will publish a summary attestation here once the
                  current assessment cycle completes.
                </p>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-sm" data-id="element-961" data-mp="vw0pys5d6q9m">
                  <ClockIcon className="w-4 h-4" strokeWidth={1.75} data-id="element-962" data-mp="1olhehepv5n0k" />
                  Details to be added once assessment completes
                </div>
              </Card>
            </section>

            {/* Incident Response */}
            <section id="incident" data-id="element-963" data-mp="1d7fdrkehjrp2">
              <SectionTitle title="Incident response" data-id="element-964" data-mp="z6l6j536bwrz" />
              <Card data-id="element-965" data-mp="z8612uphnfpu">
                <div className="space-y-5" data-id="element-966" data-mp="z8o6ohy1j6af">
                  <div data-id="element-967" data-mp="146hbb6jp3s8l">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1" data-id="element-968" data-mp="145y0sawdk64l">
                      Triage SLA
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-969" data-mp="1wu3nh36zo572">
                      We triage suspected security incidents within 24 hours of
                      detection, with an on-call engineer assigned and a working
                      assessment of impact.
                    </p>
                  </div>
                  <div data-id="element-970" data-mp="2atcrl4hx78uo">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1" data-id="element-971" data-mp="2at31lgwvl1oz">
                      Customer notification
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-972" data-mp="2fme8fi1otigw">
                      Customers affected by a confirmed incident are notified
                      via their primary admin contact, with the facts we have at
                      the time and an explicit commitment to follow-up updates
                      as we learn more.
                    </p>
                  </div>
                  <div data-id="element-973" data-mp="8yosgugelapv">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1" data-id="element-974" data-mp="3hbumd8fd2urk">
                      Material incident reporting
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-975" data-mp="3cevw50ytj7yn">
                      Material incidents — those involving confirmed
                      unauthorized access to customer data — are reported to
                      affected customers within 72 hours of confirmation.
                    </p>
                  </div>
                </div>
              </Card>
            </section>

            {/* Vulnerability Disclosure */}
            <section id="disclosure" data-id="element-976" data-mp="3ufqjbpn03g3b">
              <SectionTitle title="Vulnerability disclosure" data-id="element-977" data-mp="3ueov0jgz4x3n" />
              <Card data-id="element-978" data-mp="38x8rv6bi4m4">
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-979" data-mp="38dyn0mwskin">
                  We welcome reports from security researchers, customers, and
                  the broader community. We ask researchers to report suspected
                  issues privately to{' '}
                  <a href="mailto:security@nisa.coach" className="text-cyan-600 hover:text-cyan-700 underline underline-offset-2" data-id="element-980" data-mp="3bp8u3v1dn2mp">
                    security@nisa.coach
                  </a>{' '}
                  with steps to reproduce, and to give us a reasonable window to
                  fix the issue before public disclosure.
                </p>
                <p className="text-gray-700 leading-relaxed" data-id="element-981" data-mp="2s1jaib3lg8ok">
                  In return, we commit to an initial response within 7 business
                  days, status updates at least every 14 days while a report is
                  open, and a coordinated public disclosure timeline agreed with
                  the reporter. Researchers who act in good faith under this
                  policy are covered by our safe-harbor language.
                </p>
              </Card>
            </section>

            {/* Documents & Resources */}
            <section id="documents" data-id="element-982" data-mp="2bgixdik3yd8a">
              <SectionTitle title="Documents & resources" data-id="element-983" data-mp="26hfr5use60yq" />
              <div className="grid md:grid-cols-2 gap-4" data-id="element-984" data-mp="26j0kxi6vtfvj">
                {documents.map(doc => <a key={doc.title} href={doc.href} target={doc.external ? '_blank' : undefined} rel={doc.external ? 'noopener noreferrer' : undefined} className="group bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 shadow-sm p-6 hover:border-cyan-300 hover:shadow-md transition-all" data-id="element-985" data-mp="21owc0te22xrt">
                    <div className="flex items-start justify-between gap-4" data-id="element-986" data-mp="36ot7h4yhr2lq">
                      <div data-id="element-987" data-mp="36mq8hvfd1z4v">
                        <div className="font-medium text-gray-800 mb-1 group-hover:text-cyan-700 transition-colors" data-id="element-988" data-mp="3bnnqo80det6d">
                          {doc.title}
                        </div>
                        <div className="text-sm text-gray-600 leading-relaxed" data-id="element-989" data-mp="2xqhc6pmojgmy">
                          {doc.description}
                        </div>
                      </div>
                      <ExternalLinkIcon className="w-4 h-4 text-gray-400 group-hover:text-cyan-600 shrink-0 mt-1 transition-colors" strokeWidth={1.75} data-id="element-990" data-mp="2nw8ja0tqllo9" />
                    </div>
                  </a>)}
              </div>
            </section>

            {/* Contact */}
            <section id="contact" data-id="element-991" data-mp="24z7f2i80bpld">
              <SectionTitle title="Contact" data-id="element-992" data-mp="2508yb9z6fie6" />
              <Card data-id="element-993" data-mp="29z23mf4b8339">
                <div className="flex flex-col items-center text-center" data-id="element-994" data-mp="29ximyneqrq0y">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-50 text-cyan-600 border border-cyan-100 mb-4" data-id="element-995" data-mp="1qc8mul1bsr5l">
                    <MailIcon className="w-6 h-6" strokeWidth={1.75} data-id="element-996" data-mp="1lfi8q39305mt" />
                  </div>
                  <p className="text-gray-700 max-w-2xl mb-6 leading-relaxed" data-id="element-997" data-mp="1zf00mrhobz0e">
                    Security questionnaires, DPAs, sub-processor lists, and
                    vulnerability reports all reach us at the same address. We
                    route your request to the right person.
                  </p>
                  <a href="mailto:security@nisa.coach" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-cyan-600 text-white font-medium hover:bg-cyan-700 transition-colors" data-id="element-998" data-mp="24afbrboargt4">
                    <MailIcon className="w-4 h-4" strokeWidth={2} data-id="element-999" data-mp="2s1cblq2tino0" />
                    security@nisa.coach
                  </a>
                </div>
              </Card>
            </section>
          </div>

          <p className="text-sm text-gray-500 mt-12 text-center" data-id="element-1000" data-mp="2zkpx3xmuztel">
            Last updated: May 2026
          </p>
        </div>
      </main>
      <Footer data-id="element-1001" data-mp="2vm8lp1wenwrd" />
    </div>;
};